import PropTypes from 'prop-types';
import React, { Component } from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { CardZeroState, getFormattedDateTime } from 'ecs-react-components/dist/index.es';
import { messages } from './AppointmentsWidget.i18n';
import history from '../../history';
import { loadUpcomingAppointments } from '../../redux/modules/appointment/appointment-middleware';
import LoadingIndicatorContainer from '../LoadingIndicatorContainer';
import { callWithLoadingIndicator } from '../../redux/modules/loading-indicator/loading-indicator-middleware';

class AppointmentsWidget extends Component {
  UNSAFE_componentWillMount() {
    if (this.props.patient.patientId) {
      this.props.dispatch(
        callWithLoadingIndicator(
          'AppointmentsWidget',
          () => this.props.dispatch(loadUpcomingAppointments(this.props.patient.patientId))
        )
      );
    }
  }

  setLinkTo(appt) {
    let linkTo = '';
    if (appt.upcomingAppointmentType === 'FollowUpAppointment') {
      linkTo = '/followup-plan';
    } else if (appt.linkedToOtherTypeDatabaseId.length) {
      linkTo = '/followup-plan';
    } else {
      linkTo = '/appointments';
    }
    return linkTo;
  }

  navigateTo = (linkTo) => () => {
    if (linkTo) {
      history.push(linkTo);
    }
  }

  renderAppointmentLocation = (appt) => {
    const markup = () => ({ __html: appt.shortLocation });
    return (
      <div className="appointment-address" data-qa-item="appointmentLocationField"
        dangerouslySetInnerHTML={markup()} tabIndex={0}
      />
    );
  };

  renderViewMore(appt) {
    const linkTo = this.setLinkTo(appt);
    return (
      <a className="btn btn-link pull-right" data-qa-item="ViewMoreField"
        onClick={this.navigateTo(linkTo)} tabIndex={0}
        aria-label={ this.props.intl.formatMessage(messages.screenReader.viewMore, { page: linkTo.replace('/', '') }) }
        role="link"
      >
        <span className="hidden-xs">
          <FormattedMessage { ...messages.page.viewMore } />
        </span>
        <span className="glyphicon glyphicon-menu-right"></span>
      </a>
    );
  }

  renderFollowupLabel(yearsVal, monthsVal, appt) {
    if (!yearsVal && !monthsVal) {
      return (
      <span>
        {appt.followUpLabel}
        <FormattedMessage { ...messages.statements.followup } />
      </span>);
    }
    return (
      <span>
        { yearsVal ?
          <FormattedMessage
            { ...messages.statements.years }
            values={{ years: yearsVal }}
          /> :
          null}
        <span> </span>
        { monthsVal ?
          <FormattedMessage
            { ...messages.statements.months }
            values={{ months: monthsVal }}
          /> :
          null}
        <FormattedMessage { ...messages.statements.followup } />
      </span>
    );
  }

  renderFollowup(appt, date) {
    const monthProp = 'm';
    const yearProp = 'y';
    const yearsVal = appt.followUpLabelParts[yearProp];
    const monthsVal = appt.followUpLabelParts[monthProp];
    return (
      <div className="appointment" data-qa-container="apptContainer">
        { this.renderFollowUpDate(date) }
        <div className="appointment-details">
          { this.renderViewMore(appt) }
          <div className="appointment-type" data-qa-item="followupTypeField" tabIndex={0}>
            { this.renderFollowupLabel(yearsVal, monthsVal, appt) }
          </div>
        </div>
      </div>
    );
  }

  renderExternalAppt(appt, timeZone, daysAhead) {
    return (
      <div className="appointment" data-qa-container="apptContainer">
        { this.renderAppointmentDate(appt, timeZone, daysAhead) }
        <div className="appointment-details">
        { this.renderViewMore(appt) }
          <div className="appointment-type" data-qa-item="appointmentTypeField" tabIndex={0}>
            { appt.externalAppointmentType }
          </div>
          { this.renderAppointmentLocation(appt) }
          { appt.status !== null ? this.renderAppointmentStatus(appt.status) : null }
        </div>
      </div>
    );
  }

  renderDaysAhead(daysParam) {
    if (daysParam === 0) {
      return <FormattedMessage { ...messages.statements.today } />;
    }
    if (daysParam === 1) {
      return <FormattedMessage { ...messages.statements.tomorrow } />;
    }
    return (<FormattedMessage
      { ...messages.statements.daysAhead }
      values={{ days: daysParam }}
    />);
  }

  renderDaysAheadLabel(daysAhead) {
    return (
      daysAhead >= 0 && daysAhead < 8 ?
          <span className="accent-color">
            { this.renderDaysAhead(daysAhead) }
          </span> :
        null
    );
  }

  renderFollowUpDate(date) {
    const dateParts = getFormattedDateTime(null, date, this.props.language);
    return (
      <div className="appointment-date" data-qa-item="dateField" tabIndex={0}>
          { dateParts.datePart }
        <div className="accent-color">
          <FormattedMessage { ...messages.statements.recommended } />
        </div>
      </div>
    );
  }

  renderAppointmentStatus(status) {
    return (
      <div className="appointment-status" data-qa-item="appointmentStatus">
        <span className="appointment-statusLabel"><FormattedMessage { ...messages.statements.status } />: </span>
        <span className="appointment-status">{ status.length > 30 ? status.substring(0, 30) + '...' : status }</span>
      </div>
    );
  }

  renderAppointmentDate(appt, timeZone, daysAhead) {
    const label = appt.hasTimePart && (timeZone !== appt.timeZoneAbbreviation) ?
      ' ' + appt.timeZoneAbbreviation :
      '';
    const dateParts = getFormattedDateTime(appt.date, appt.date, this.props.language);
    return (
      <div className="appointment-date" data-qa-item="dateField" tabIndex={0}>
          { dateParts.datePart }
        <div className="appointment-time" >
          { dateParts.timePart } { label }
        </div>
        { this.renderDaysAheadLabel(daysAhead) }
      </div>
    );
  }

  render() {
    const { capabilitySetting, contactUs, upcomingAppointments, timeZone } = this.props;
    const { formatMessage } = this.props.intl;

    const hasRequestCapability = () => {
      const hasLinkCap = capabilitySetting && capabilitySetting.details ?
        !!capabilitySetting.details.find((x) => x.fieldName === 'RequestNewAppointment') : false;
      return contactUs && hasLinkCap;
    };
    return (
      <div className="col-md-6 dashboard-module appointment-list">
        <LoadingIndicatorContainer loadingKey="AppointmentsWidget" size="small">
          <div className="row">
            <div className="col-xs-6">
              <h2 tabIndex="0" className="box-title" data-qa-item="widgetHeader">
                <FormattedMessage { ...messages.page.header } />
              </h2>
            </div>
            <div className="col-xs-6">
              {
                hasRequestCapability() ?
                <a className="btn btn-link theme pull-right"
                  onClick={this.navigateTo('/contact-us')}
                  data-qa-item="requestAppointmentLink" tabIndex="0"
                  aria-label={ formatMessage(messages.screenReader.requestAppointment) }
                  role="link"
                >
                  <span className="fa fa-calendar"></span>
                  <span className="hidden-xs">
                    <FormattedMessage { ...messages.page.requestNew } />
                  </span>
                </a> :
                  null
              }
            </div>
          </div>
          {
          upcomingAppointments.length ?
          <div data-qa-container="apptsListContainer">
            { upcomingAppointments.map((appt) => {
              const daysAhead = moment(appt.rawDate).startOf('day').diff(moment().startOf('day'), 'days');
              return (
                <div className="row" key={appt.id + appt.upcomingAppointmentType}>
                  <div className="col-xs-12 appointment-container">
                    {
                      appt.upcomingAppointmentType === 'FollowUpAppointment' ?
                        this.renderFollowup(appt, appt.date) :
                        this.renderExternalAppt(appt, timeZone, daysAhead)
                    }
                  </div>
                </div>
              );
            })}
            { upcomingAppointments.some((x) => x.hasTimePart) ?
              <h6 data-qa-item="timeZoneWarning" tabIndex={0}>
                <FormattedMessage
                  { ...messages.statements.timezone }
                  values={{ facilityTimeZone: this.props.timeZone }}
                />
              </h6> :
              null}
            <div className="spacer small"></div>
          </div> :
          <CardZeroState
            showZeroHeader={false}
            zeroStateMessage={ <FormattedMessage { ...messages.statements.noAppointments } /> }
            stateIconCss="fa-calendar"
          />
          }
        </LoadingIndicatorContainer>
      </div>
    );
  }
}

AppointmentsWidget.propTypes = {
  dispatch: PropTypes.func.isRequired,
  upcomingAppointments: PropTypes.array,
  patient: PropTypes.object,
  timeZone: PropTypes.string,
  language: PropTypes.string,
  capabilitySetting: PropTypes.object,
  contactUs: PropTypes.object,
  intl: PropTypes.object,
};

const mapStateToProps = (state) => ({
  upcomingAppointments: state.appointment.upcomingAppointments,
  patient: state.profile.patient,
  timeZone: state.facility.timeZoneAbbreviation,
  language: state.language.languageCode,
});

export default connect(mapStateToProps)(injectIntl(AppointmentsWidget));